import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Plus, Pencil, Check, Copy, Trash2, MoreHorizontal, ChevronDown, Calendar, LayoutDashboard, Loader2, RefreshCw, GripVertical, Sparkles, X, Undo2, Aperture,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  api, clearQueryCache, DATE_PRESETS, WIDGET_TYPES, newId, rangeLabel, makeCtx, newWidget, normalizeWidget, sourceLogo,
  aiToWidget, aiDateRange, widgetToAi, placeWidgets,
} from './lib';
import { WidgetBody } from './Widgets';
import { Logo } from './Fields';
import WidgetEditor from './WidgetEditor';
import { TypeIcon } from './TypeIcon';

const COLS = 12;
const ROW_HEIGHT = 28;

// WidthProvider ilk ölçümde sidebar'lı düzenin genişliğini yanlış alıyor; kendimiz ölçüyoruz.
function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  });
  return [ref, width];
}

// ─── Küçük yardımcı bileşenler ───────────────────────────────────────────────
function useClickOutside(ref, onOutside, active) {
  useEffect(() => {
    if (!active) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onOutside, active]);
}

function Menu({ button, children, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close, open);
  return (
    <div className="relative" ref={ref}>
      {button({ open, toggle: () => setOpen((o) => !o) })}
      {open && (
        <div className={`absolute z-40 mt-1 min-w-[200px] rounded-xl border border-gray-200 bg-white shadow-xl py-1 ${align === 'right' ? 'right-0' : 'left-0'}`} onClick={close}>
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, children, onClick, danger }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${danger ? 'text-rose-600 hover:bg-rose-50' : 'text-gray-700 hover:bg-gray-50'}`}>
      {Icon && <Icon className="w-4 h-4" />}{children}
    </button>
  );
}

function DateRangePicker({ value, onChange }) {
  return (
    <Menu button={({ toggle }) => (
      <button type="button" onClick={toggle} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50">
        <Calendar className="w-4 h-4 text-gray-500" />{rangeLabel(value)}<ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
    )}>
      {DATE_PRESETS.filter((p) => p.key !== 'custom').map((p) => (
        <MenuItem key={p.key} onClick={() => onChange({ preset: p.key })}>
          <span className={value?.preset === p.key ? 'font-semibold text-gray-900' : ''}>{p.label}</span>
        </MenuItem>
      ))}
      <div className="border-t border-gray-100 mt-1 pt-2 px-3 pb-2" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-medium text-gray-500 mb-1.5">Custom range</div>
        <div className="flex flex-col gap-1.5">
          <input type="date" className="rounded-md border border-gray-300 px-2 py-1 text-sm" value={value?.preset === 'custom' ? value.start || '' : ''}
            onChange={(e) => onChange({ preset: 'custom', start: e.target.value, end: value?.end })} />
          <input type="date" className="rounded-md border border-gray-300 px-2 py-1 text-sm" value={value?.preset === 'custom' ? value.end || '' : ''}
            onChange={(e) => onChange({ preset: 'custom', start: value?.start, end: e.target.value })} />
        </div>
      </div>
    </Menu>
  );
}

function autoTitle(w, ctx) {
  if (w.title) return w.title;
  if (w.type === 'text') return '';
  if (!w.metrics?.length) return 'New widget';
  const label = (r) => ctx.metric(r).fullLabel;
  const m = label(w.metrics[0]);
  if (w.type === 'kpi') return m;
  if (w.type === 'pivot') return `${m} · ${ctx.dim(w.pivotRow).label} × ${ctx.dim(w.pivotCol).label}`;
  const by = w.breakdown || w.dimension;
  const shown = w.breakdown ? [w.metrics[0]] : w.metrics;
  const names = shown.length > 3 ? `${shown.slice(0, 3).map(label).join(', ')} +${shown.length - 3}` : shown.map(label).join(', ');
  return `${names}${by ? ` · ${ctx.dim(by).label}` : ''}`;
}

// ─── AI ile tarif ────────────────────────────────────────────────────────────
const AI_EXAMPLE = 'e.g. "Spend and ROAS per platform as cards, a weekly spend chart by platform below, then the top 15 campaigns by spend"';

function AskAI({ busy, result, onSubmit, onUndo, onDismiss, big }) {
  const [text, setText] = useState('');
  const submit = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || busy) return;
    if (await onSubmit(v)) setText('');
  };
  return (
    <div className={big ? 'max-w-2xl mx-auto text-left' : ''}>
      <form onSubmit={submit} className="flex items-start gap-2 rounded-xl border border-violet-200 bg-white px-3 py-2 shadow-sm focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-500/20">
        <Sparkles className="w-4 h-4 text-violet-600 mt-2 shrink-0" />
        <textarea rows={big ? 3 : 1} value={text} disabled={busy}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submit(e); }}
          placeholder={big ? `Describe the report you want, ${AI_EXAMPLE}` : 'Describe a widget or a whole report and AI will build it…'}
          className="flex-1 resize-none bg-transparent text-sm text-gray-900 placeholder:text-gray-400 outline-none py-1.5 disabled:opacity-60" />
        <button type="submit" disabled={busy || !text.trim()}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-40">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Building…</> : 'Build'}
        </button>
      </form>
      {result && (
        <div className={`mt-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${result.error ? 'bg-rose-50 text-rose-700' : 'bg-violet-50 text-violet-900'}`}>
          <span className="flex-1">{result.text}</span>
          {result.undo && (
            <button type="button" onClick={onUndo} className="inline-flex items-center gap-1 font-medium text-violet-700 hover:text-violet-900"><Undo2 className="w-3.5 h-3.5" />Undo</button>
          )}
          <button type="button" onClick={onDismiss} className="text-gray-400 hover:text-gray-700" aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

const TEXT_BG = {
  white: 'bg-white border-gray-200 shadow-sm',
  none: 'bg-transparent border-transparent',
  blue: 'bg-blue-50 border-blue-100',
  dark: 'bg-gray-900 border-gray-900',
};

// ─── Widget kartı ────────────────────────────────────────────────────────────
function WidgetCard({ w, sourcesByKey, editing, selected, brandId, range, currency, refreshKey, onEdit, onDuplicate, onDelete, onChange }) {
  const ctx = useMemo(() => makeCtx(w, sourcesByKey), [w, sourcesByKey]);
  const title = autoTitle(w, ctx);
  const bare = w.type === 'text';
  const parts = w.parts || [];
  const stop = (e) => e.stopPropagation();
  const frame = bare
    ? `${TEXT_BG[w.textBg || 'white']} ${editing && (w.textBg === 'none') ? '!border-dashed !border-gray-300' : ''}`
    : 'bg-white border-gray-200 shadow-sm';
  return (
    <div className={`group/card relative h-full flex flex-col rounded-2xl border transition-shadow ${frame} ${selected ? '!border-blue-500 ring-2 ring-blue-500/20' : ''} ${editing && !bare ? 'hover:shadow-md' : ''}`}>
      {!bare && (title || editing) && (
        <div className={`flex items-center gap-1.5 px-4 pt-3 pb-2 ${editing ? 'widget-drag cursor-move' : ''}`}>
          {editing && <GripVertical className="w-4 h-4 text-gray-300 shrink-0 -ml-1.5" />}
          <h3 className="text-sm font-medium text-gray-800 truncate flex-1" title={title}>{title}</h3>
          {w.type !== 'kpi' && parts.length > 0 && (
            <span className="hidden md:inline-flex items-center gap-1 text-[11px] text-gray-400 truncate max-w-[40%]">
              {parts.map((p) => <Logo key={p.id} name={sourceLogo(p.source)} sourceKey={p.source} className="w-3.5 h-3.5" />)}
              {parts.length === 1 && <span className="truncate">{sourcesByKey[parts[0].source]?.label}</span>}
              {w.dateMode === 'custom' && <span className="truncate">· {rangeLabel(w.dateRange)}</span>}
            </span>
          )}
          {editing && (
            <div className="flex items-center" onMouseDown={stop}>
              <button type="button" onClick={onEdit} className="p-1 rounded-md text-gray-400 hover:text-gray-800 hover:bg-gray-100" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
              <Menu button={({ toggle }) => (
                <button type="button" onClick={toggle} className="p-1 rounded-md text-gray-400 hover:text-gray-800 hover:bg-gray-100" aria-label="More"><MoreHorizontal className="w-4 h-4" /></button>
              )}>
                <MenuItem icon={Copy} onClick={onDuplicate}>Duplicate</MenuItem>
                <MenuItem icon={Trash2} onClick={onDelete} danger>Delete</MenuItem>
              </Menu>
            </div>
          )}
        </div>
      )}
      {/* Metin widget'ı: tutamaç kutusu tıklamayı yutmamalı, yoksa ızgara sürüklemeyi hiç görmez. */}
      {bare && editing && (
        <div className="absolute top-1.5 right-1.5 z-10 flex items-center rounded-lg bg-white shadow-sm border border-gray-200 opacity-0 group-hover/card:opacity-100 transition-opacity">
          <span className="widget-drag cursor-move p-1 text-gray-400 hover:text-gray-700" title="Move"><GripVertical className="w-3.5 h-3.5" /></span>
          <button type="button" onMouseDown={stop} onClick={onEdit} className="p-1 rounded-md text-gray-400 hover:text-gray-800" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={stop} onClick={onDuplicate} className="p-1 rounded-md text-gray-400 hover:text-gray-800" aria-label="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={stop} onClick={onDelete} className="p-1 rounded-md text-gray-400 hover:text-rose-600" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <div className={`flex-1 min-h-0 px-4 ${bare ? `py-2 ${editing ? 'widget-drag cursor-move' : ''}` : 'pb-4'}`} onMouseDown={(e) => editing && !bare && e.stopPropagation()}>
        <WidgetBody w={w} sourcesByKey={sourcesByKey} brandId={brandId} range={range} currency={currency} refreshKey={refreshKey} onChange={onChange} />
      </div>
    </div>
  );
}

// ─── Sayfa ───────────────────────────────────────────────────────────────────
export default function CustomReports() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [sources, setSources] = useState([]);
  const [currency, setCurrency] = useState('TRY');
  const [reports, setReports] = useState(null);
  const [report, setReport] = useState(null); // { id, name, config }
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [saveState, setSaveState] = useState('saved'); // saved | dirty | saving | error
  const [renaming, setRenaming] = useState(false);
  const [narrowWindow, setNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [refreshKey, setRefreshKey] = useState(0);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { text, error, undo }
  const lastSaved = useRef('');
  const [gridRef, gridWidth] = useElementWidth();

  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Marka değişince kaynaklar ve rapor listesi yeniden.
  useEffect(() => {
    if (!brandId) return;
    setSources([]); setReports(null); setLoadError(null);
    clearQueryCache();
    api.sources(brandId).then((d) => { setSources(d.sources); setCurrency(d.currency || 'TRY'); }).catch((e) => setLoadError(e.message));
    api.list(brandId).then((d) => setReports(d.reports)).catch((e) => setLoadError(e.message));
  }, [brandId]);

  // Sidebar'daki Glowy Lens alt menüsü bu listeyi gösteriyor; her değişiklikte haber ver.
  useEffect(() => {
    if (brandId && reports) window.dispatchEvent(new CustomEvent('glowy-lens:reports', { detail: { brandId, reports } }));
  }, [brandId, reports]);

  // Adresteki rapor yoksa ilkine git.
  useEffect(() => {
    if (!reports) return;
    if (!reportId && reports.length) navigate(`/custom-reports/${reports[0].id}`, { replace: true });
    if (reportId && !reports.some((r) => r.id === reportId) && reports.length) navigate(`/custom-reports/${reports[0].id}`, { replace: true });
  }, [reports, reportId, navigate]);

  // Liste her kayıtta güncelleniyor; rapor yalnızca adres değişince yeniden yüklensin.
  const reportExists = !!reports?.some((r) => r.id === reportId);
  useEffect(() => {
    if (!brandId || !reportId || !reportExists) { setReport(null); return undefined; }
    let cancelled = false;
    api.get(brandId, reportId).then(({ report: r }) => {
      if (cancelled) return;
      const config = { dateRange: { preset: 'last30' }, widgets: [], ...(r.config || {}) };
      config.widgets = config.widgets.map(normalizeWidget);
      lastSaved.current = JSON.stringify({ name: r.name, config });
      setReport({ id: r.id, name: r.name, config });
      setSaveState('saved');
      setEditing(config.widgets.length === 0);
      setEditingWidgetId(null);
    }).catch((e) => setLoadError(e.message));
    return () => { cancelled = true; };
  }, [brandId, reportId, reportExists]);

  // Otomatik kayıt: değişiklikten 800 ms sonra.
  useEffect(() => {
    if (!report) return undefined;
    const snapshot = JSON.stringify({ name: report.name, config: report.config });
    if (snapshot === lastSaved.current) return undefined;
    setSaveState('dirty');
    const t = setTimeout(async () => {
      setSaveState('saving');
      try {
        await api.update(brandId, report.id, { name: report.name, config: report.config });
        lastSaved.current = snapshot;
        setSaveState('saved');
        setReports((list) => list?.map((r) => (r.id === report.id ? { ...r, name: report.name, widget_count: report.config.widgets.length } : r)));
      } catch (e) {
        setSaveState('error');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [report, brandId]);

  const updateConfig = useCallback((fn) => {
    setReport((r) => (r ? { ...r, config: fn(r.config) } : r));
  }, []);
  const updateWidget = useCallback((id, patch) => {
    updateConfig((c) => ({ ...c, widgets: c.widgets.map((w) => (w.id === id ? { ...w, ...patch } : w)) }));
  }, [updateConfig]);

  const sourceByKey = useMemo(() => Object.fromEntries(sources.map((s) => [s.key, s])), [sources]);
  const defaultSource = sources.find((s) => s.key === 'ads_all' && s.connected) || sources.find((s) => s.connected);

  const addWidget = (type) => {
    const t = WIDGET_TYPES.find((x) => x.key === type);
    const id = newId();
    const w = {
      id, title: '',
      ...newWidget(type, defaultSource, sourceByKey),
      layout: { x: 0, y: Infinity, w: t.size.w, h: t.size.h },
    };
    updateConfig((c) => ({ ...c, widgets: [...c.widgets, w] }));
    setEditing(true);
    setEditingWidgetId(id);
  };

  const duplicateWidget = (w) => {
    const id = newId();
    updateConfig((c) => ({ ...c, widgets: [...c.widgets, { ...w, id, layout: { ...w.layout, y: Infinity } }] }));
  };
  const deleteWidget = (id) => {
    updateConfig((c) => ({ ...c, widgets: c.widgets.filter((w) => w.id !== id) }));
    if (editingWidgetId === id) setEditingWidgetId(null);
  };

  const onLayoutChange = (layout) => {
    if (narrow) return;
    const byId = Object.fromEntries(layout.map((l) => [l.i, l]));
    updateConfig((c) => {
      let changed = false;
      const widgets = c.widgets.map((w) => {
        const l = byId[w.id];
        if (!l) return w;
        const next = { x: l.x, y: l.y, w: l.w, h: l.h };
        if (w.layout && w.layout.x === next.x && w.layout.y === next.y && w.layout.w === next.w && w.layout.h === next.h) return w;
        changed = true;
        return { ...w, layout: next };
      });
      return changed ? { ...c, widgets } : c;
    });
  };

  const createReport = async () => {
    const n = (reports?.length || 0) + 1;
    const { report: r } = await api.create(brandId, { name: `Report ${n}`, config: { dateRange: { preset: 'last30' }, widgets: [] } });
    setReports((list) => [{ id: r.id, name: r.name, widget_count: 0, updated_at: r.updated_at }, ...(list || [])]);
    navigate(`/custom-reports/${r.id}`);
  };
  const duplicateReport = async () => {
    const { report: r } = await api.duplicate(brandId, report.id);
    setReports((list) => [{ id: r.id, name: r.name, widget_count: r.config?.widgets?.length || 0, updated_at: r.updated_at }, ...(list || [])]);
    navigate(`/custom-reports/${r.id}`);
  };
  const deleteReport = async () => {
    if (!window.confirm(`Delete "${report.name}"? This can't be undone.`)) return;
    await api.remove(brandId, report.id);
    const rest = reports.filter((r) => r.id !== report.id);
    setReports(rest);
    navigate(rest.length ? `/custom-reports/${rest[0].id}` : '/custom-reports', { replace: true });
  };
  // AI: tarif → widget'lar. Model raporun tamamını görür; yeni ekler, var olanı
  // yerinde değiştirir (replaceIndex) ya da siler (remove). Seçili widget ipucudur.
  const runAi = async (prompt, target) => {
    setAiBusy(true);
    setAiResult(null);
    try {
      const current = report.config.widgets;
      const body = {
        prompt,
        report: {
          dateRange: report.config.dateRange,
          widgets: current.map((w, i) => ({
            index: i,
            shownTitle: autoTitle(w, makeCtx(w, sourceByKey)) || w.text || '',
            ...widgetToAi(w),
          })),
        },
      };
      if (target) body.editingIndex = current.findIndex((w) => w.id === target.id);
      const r = await api.ai(brandId, body);
      const notes = [];
      const items = (r.widgets || []).map((a) => ({ index: Number.isInteger(a.replaceIndex) ? a.replaceIndex : -1, w: aiToWidget(a, sourceByKey, notes) }))
        .filter((x) => x.w);
      const before = report.config;
      let applied = false;

      if (r.action === 'replace' && items.length) {
        updateConfig((c) => ({ ...c, widgets: placeWidgets([], items.map((x) => x.w)) }));
        setEditingWidgetId(null);
        applied = true;
      } else if (r.action !== 'none') {
        // Eski "edit" cevabı: tek widget, seçili olanın yerine.
        const editIdx = target ? current.findIndex((w) => w.id === target.id) : -1;
        if (r.action === 'edit' && editIdx >= 0 && items[0]) items[0].index = editIdx;
        const replaced = new Map(items.filter((x) => x.index >= 0 && x.index < current.length).map((x) => [x.index, x.w]));
        const added = items.filter((x) => !(x.index >= 0 && x.index < current.length)).map((x) => x.w);
        const removed = new Set((r.remove || []).filter((i) => Number.isInteger(i) && i >= 0 && i < current.length && !replaced.has(i)));
        if (replaced.size || added.length || removed.size) {
          updateConfig((c) => {
            const kept = c.widgets
              .map((w, i) => {
                if (removed.has(i)) return null;
                if (!replaced.has(i)) return w;
                const { size, ...nw } = replaced.get(i);
                return { ...nw, id: w.id, layout: w.layout };
              })
              .filter(Boolean);
            return { ...c, widgets: [...kept, ...placeWidgets(kept, added)] };
          });
          if (target && removed.has(current.findIndex((w) => w.id === target.id))) setEditingWidgetId(null);
          applied = true;
        }
      }
      const range = aiDateRange(r.reportDateRange?.preset ?? r.reportDateRange, r.reportDateRange?.start, r.reportDateRange?.end);
      if (range) {
        updateConfig((c) => ({ ...c, dateRange: range }));
        applied = true;
      }
      const text = [r.reply || (applied ? 'Done.' : 'Nothing was changed.'), ...new Set(notes)].join(' ');
      setAiResult({ text, undo: applied ? before : null });
      return true;
    } catch (e) {
      setAiResult({ text: e.message, error: true });
      return false;
    } finally {
      setAiBusy(false);
    }
  };
  const undoAi = () => {
    const prev = aiResult?.undo;
    if (!prev) return;
    updateConfig(() => prev);
    setAiResult(null);
  };

  const refresh = () => {
    clearQueryCache();
    setRefreshKey((k) => k + 1);
  };

  const range = report?.config.dateRange;
  const widgets = report?.config.widgets || [];
  const editingWidget = widgets.find((w) => w.id === editingWidgetId);
  const narrow = narrowWindow || (gridWidth > 0 && gridWidth < 640);
  const layout = widgets.map((w, i) => ({
    i: w.id,
    ...(narrow ? { x: 0, y: i * 10, w: COLS, h: w.layout?.h || 8 } : { x: w.layout?.x ?? 0, y: w.layout?.y ?? i * 10, w: w.layout?.w || 6, h: w.layout?.h || 8 }),
    minW: 2, minH: w.type === 'text' ? 2 : 4,
  }));

  if (!brandId) return <div className="p-8 text-sm text-gray-500">Select a brand first.</div>;

  return (
    <div className={`min-h-full ${editingWidget ? 'sm:pr-[380px]' : ''}`}>
      {/* Üst çubuk */}
      <div className="sticky top-0 z-30 bg-[#f6f8ff]/95 backdrop-blur border-b border-gray-200">
        <div className="px-4 md:px-6 py-3 flex flex-wrap items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1.5 pr-1 text-sm font-semibold text-gray-900"><Aperture className="w-5 h-5 text-indigo-500" />Glowy Lens</span>
          <Menu align="left" button={({ toggle }) => (
            <button type="button" onClick={toggle} className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100">
              Reports<ChevronDown className="w-4 h-4" />
            </button>
          )}>
            {(reports || []).map((r) => (
              <MenuItem key={r.id} onClick={() => navigate(`/custom-reports/${r.id}`)}>
                <span className={`flex-1 truncate ${r.id === reportId ? 'font-semibold text-gray-900' : ''}`}>{r.name}</span>
                <span className="text-xs text-gray-400">{r.widget_count}</span>
              </MenuItem>
            ))}
            {reports?.length > 0 && <div className="border-t border-gray-100 my-1" />}
            <MenuItem icon={Plus} onClick={createReport}>New report</MenuItem>
          </Menu>
          <span className="text-gray-300">/</span>
          {report && (renaming ? (
            <input autoFocus className="rounded-md border border-blue-400 px-2 py-1 text-base font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              defaultValue={report.name}
              onBlur={(e) => { const v = e.target.value.trim(); if (v) setReport((r) => ({ ...r, name: v })); setRenaming(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenaming(false); }} />
          ) : (
            <button type="button" onClick={() => setRenaming(true)} className="text-base font-semibold text-gray-900 hover:bg-gray-100 rounded-md px-2 py-1 truncate max-w-[50vw]" title="Rename">{report.name}</button>
          ))}
          {report && (
            <span className="text-xs text-gray-400 ml-1">
              {saveState === 'saving' || saveState === 'dirty' ? 'Saving…' : saveState === 'error' ? <span className="text-rose-600">Couldn't save</span> : 'Saved'}
            </span>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {report && <DateRangePicker value={range} onChange={(v) => updateConfig((c) => ({ ...c, dateRange: v }))} />}
            {report && (
              <button type="button" onClick={refresh} className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50" aria-label="Refresh" title="Refresh data">
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            {report && editing && (
              <Menu button={({ toggle }) => (
                <button type="button" onClick={toggle} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50">
                  <Plus className="w-4 h-4" />Add widget
                </button>
              )}>
                {WIDGET_TYPES.map((t) => (
                  <MenuItem key={t.key} onClick={() => addWidget(t.key)}>
                    <TypeIcon type={t.key} className="w-4 h-4 text-gray-500" />{t.label}
                  </MenuItem>
                ))}
              </Menu>
            )}
            {report && (
              <button type="button" onClick={() => { setEditing((e) => !e); setEditingWidgetId(null); }}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${editing ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-900 text-white hover:bg-gray-800'}`}>
                {editing ? <><Check className="w-4 h-4" />Done</> : <><Pencil className="w-4 h-4" />Edit</>}
              </button>
            )}
            {report && (
              <Menu button={({ toggle }) => (
                <button type="button" onClick={toggle} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Report menu"><MoreHorizontal className="w-4 h-4" /></button>
              )}>
                <MenuItem icon={Pencil} onClick={() => setRenaming(true)}>Rename</MenuItem>
                <MenuItem icon={Copy} onClick={duplicateReport}>Duplicate report</MenuItem>
                <MenuItem icon={Plus} onClick={createReport}>New report</MenuItem>
                <MenuItem icon={Trash2} onClick={deleteReport} danger>Delete report</MenuItem>
              </Menu>
            )}
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="px-2 md:px-4 py-3">
        {report && widgets.length > 0 && (
          <div className="px-2 pt-1 pb-2">
            <AskAI busy={aiBusy} result={aiResult} onSubmit={(p) => runAi(p)} onUndo={undoAi} onDismiss={() => setAiResult(null)} />
          </div>
        )}
        {loadError && <div className="m-4 rounded-lg bg-rose-50 text-rose-700 text-sm px-4 py-3">{loadError}</div>}
        {!reports && !loadError && <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}

        {reports && reports.length === 0 && (
          <div className="max-w-md mx-auto text-center py-24">
            <LayoutDashboard className="w-10 h-10 text-gray-300 mx-auto" />
            <h2 className="mt-4 text-lg font-semibold text-gray-900">No custom reports yet</h2>
            <p className="mt-1 text-sm text-gray-500">Build your own report page from your connected sources with KPI cards, charts, tables and pivots.</p>
            <button type="button" onClick={createReport} className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800">
              <Plus className="w-4 h-4" />Create your first report
            </button>
          </div>
        )}

        {report && widgets.length === 0 && (
          <div className="max-w-2xl mx-auto text-center py-16">
            <h2 className="text-base font-semibold text-gray-900">This report is empty</h2>
            <p className="mt-1 text-sm text-gray-500">Describe what you want to see and AI will build it, or pick a widget to start by hand.</p>
            <div className="mt-6">
              <AskAI big busy={aiBusy} result={aiResult} onSubmit={(p) => runAi(p)} onUndo={undoAi} onDismiss={() => setAiResult(null)} />
            </div>
            <div className="mt-8 mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Or add a widget</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {WIDGET_TYPES.map((t) => (
                <button key={t.key} type="button" onClick={() => addWidget(t.key)}
                  className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-4 text-xs text-gray-700 hover:border-blue-400 hover:bg-blue-50/40">
                  <TypeIcon type={t.key} className="w-5 h-5 text-gray-500" />{t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={gridRef}>
        {report && widgets.length > 0 && gridWidth > 0 && (
          <GridLayout
            width={gridWidth}
            className="cr-grid"
            layout={layout}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            margin={[16, 16]}
            containerPadding={[8, 8]}
            isDraggable={editing && !narrow}
            isResizable={editing && !narrow}
            resizeHandles={['se', 'e', 's', 'w', 'sw']}
            draggableHandle=".widget-drag"
            compactType="vertical"
            onLayoutChange={onLayoutChange}
          >
            {widgets.map((w) => (
              <div key={w.id}>
                <WidgetCard
                  w={w}
                  sourcesByKey={sourceByKey}
                  editing={editing}
                  selected={editingWidgetId === w.id}
                  brandId={brandId}
                  range={range}
                  currency={currency}
                  refreshKey={refreshKey}
                  onEdit={() => setEditingWidgetId(w.id)}
                  onDuplicate={() => duplicateWidget(w)}
                  onDelete={() => deleteWidget(w.id)}
                  onChange={(patch) => updateWidget(w.id, patch)}
                />
              </div>
            ))}
          </GridLayout>
        )}
        </div>
      </div>

      {editingWidget && sources.length > 0 && (
        <WidgetEditor
          widget={editingWidget}
          sources={sources}
          onChange={(patch) => updateWidget(editingWidget.id, patch)}
          onClose={() => setEditingWidgetId(null)}
          aiBusy={aiBusy}
          onAiEdit={(p) => runAi(p, editingWidget)}
        />
      )}

      <style>{`
        .cr-grid .react-grid-item.react-grid-placeholder { background: #2a78d6; opacity: .12; border-radius: 16px; }
        .cr-grid .react-grid-item.react-draggable-dragging, .cr-grid .react-grid-item.resizing { z-index: 20; }
        .cr-grid .react-grid-item > .react-resizable-handle.react-resizable-handle {
          background-image: none; transform: none; margin: 0; padding: 0; opacity: 0; transition: opacity .15s; z-index: 5;
        }
        .cr-grid .react-grid-item:hover > .react-resizable-handle.react-resizable-handle,
        .cr-grid .react-grid-item.resizing > .react-resizable-handle.react-resizable-handle { opacity: 1; }
        .cr-grid .react-grid-item > .react-resizable-handle::after { content: ''; position: absolute; border: none; border-radius: 2px; background: #2a78d6; transform: none; }
        .cr-grid .react-grid-item > .react-resizable-handle.react-resizable-handle-e { top: 0; bottom: 0; right: -7px; left: auto; width: 14px; height: auto; cursor: ew-resize; }
        .cr-grid .react-grid-item > .react-resizable-handle.react-resizable-handle-w { top: 0; bottom: 0; left: -7px; right: auto; width: 14px; height: auto; cursor: ew-resize; }
        .cr-grid .react-grid-item > .react-resizable-handle.react-resizable-handle-s { left: 0; right: 0; bottom: -7px; top: auto; width: auto; height: 14px; cursor: ns-resize; }
        .cr-grid .react-grid-item > .react-resizable-handle.react-resizable-handle-se { right: -7px; bottom: -7px; left: auto; top: auto; width: 16px; height: 16px; cursor: nwse-resize; }
        .cr-grid .react-grid-item > .react-resizable-handle.react-resizable-handle-sw { left: -7px; bottom: -7px; right: auto; top: auto; width: 16px; height: 16px; cursor: nesw-resize; }
        .cr-grid .react-resizable-handle-e::after, .cr-grid .react-resizable-handle-w::after { top: 50%; left: 5px; right: auto; bottom: auto; width: 4px; height: 36px; margin-top: -18px; }
        .cr-grid .react-resizable-handle-s::after { left: 50%; top: 5px; right: auto; bottom: auto; width: 36px; height: 4px; margin-left: -18px; }
        .cr-grid .react-resizable-handle-se::after, .cr-grid .react-resizable-handle-sw::after { top: 4px; left: 4px; right: auto; bottom: auto; width: 8px; height: 8px; border-radius: 50%; background: #fff; box-shadow: 0 0 0 2px #2a78d6; }
      `}</style>
    </div>
  );
}
